import React, { useState, useMemo, useEffect } from "react"
import {
    DataGrid,
    GridColumnMenuColumnsItem,
    GridColumnMenuContainer,
    GridColumnMenuFilterItem,
    GridColumnMenuSortItem,
    useGridApiRef,
} from "@mui/x-data-grid"
import PropTypes from "prop-types"
import { styled } from "@mui/material/styles"
import { Box, Typography, Button } from "@mui/material"
import { ReactComponent as TableBaseIcon } from "src/v2/assets/icons/table-base-icon.svg"
import { localeBRDataGridText } from "src/utils/data-grid-local-text"
import { deepmerge } from "@mui/utils"
import CustomLoadingOverlay from "./custom-loading-overlay"
import SelectComponent from "../select/select-component"

const ButtonPagination = ({ value, handleClick, isActive }) => (
    <Button
        onClick={handleClick}
        sx={{
            border: "1px solid #FFFFFF",
            width: "48px",
            height: "48px",
            color: "#FFFFFF",
            padding: 0,
            minWidth: "40px",
            backgroundColor: isActive ? "#24346C" : "transparent",
            "&:hover": {
                backgroundColor: isActive ? "#24346C" : "#FFFFFF22",
            },
        }}
    >
        {value}
    </Button>
)

ButtonPagination.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleClick: PropTypes.func,
    isActive: PropTypes.bool,
}

const FooterPagination = ({
    totalPages,
    page,
    handlePageChange,
    handlePageSizeChange,
    valueSelectPaginationValue,
}) => {
    const pagesToShow = Math.min(totalPages, 3)
    const startPage = page

    const pageNumbers = Array.from({ length: pagesToShow }, (_, i) => startPage + i)
        .filter((p) => p <= totalPages && p > 0)

    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
                <ButtonPagination
                    handleClick={() => handlePageChange(Math.max(1, page - 1))}
                    value="<"
                />

                {pageNumbers.map((p) => (
                    <ButtonPagination
                        key={p}
                        value={p}
                        isActive={p === page}
                        handleClick={() => handlePageChange(p)}
                    />
                ))}

                <ButtonPagination
                    handleClick={() =>
                        handlePageChange(Math.min(totalPages, page + 1))
                    }
                    value=">"
                />
            </Box>

            <SelectComponent
                label="itens por página"
                value={valueSelectPaginationValue}
                onChange={(e) =>
                    handlePageSizeChange(Number(e.target.value))
                }
                options={["25", "50", "100"]}
                width="138px"
            />
        </Box>
    )
}

FooterPagination.propTypes = {
    totalPages: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    handlePageChange: PropTypes.func.isRequired,
    handlePageSizeChange: PropTypes.func.isRequired,
    valueSelectPaginationValue: PropTypes.number.isRequired,
}

const StyledGridColumnMenuContainer = styled(GridColumnMenuContainer)(() => ({
    backgroundColor: "#2E3448",
    "& .MuiMenuItem-root": {
        "& .MuiTypography-root": {
            fontSize: "14px",
            color: "#fff",
        },
        "&:hover": { backgroundColor: "#14192B" },
    },
    "& .MuiListItemIcon-root": {
        color: "#E3A107",
    },
}))

function CustomColumnMenu(props) {
    const itemProps = { colDef: props.colDef, onClick: props.hideMenu }

    return (
        <StyledGridColumnMenuContainer {...props}>
            <GridColumnMenuSortItem {...itemProps} />
            <GridColumnMenuFilterItem {...itemProps} />
            <GridColumnMenuColumnsItem {...itemProps} />
        </StyledGridColumnMenuContainer>
    )
}

CustomColumnMenu.propTypes = {
    colDef: PropTypes.object.isRequired,
    hideMenu: PropTypes.func.isRequired,
}

const NoRowOverlay = () => (
    <Box
        sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            gap: "12px",
        }}
    >
        <TableBaseIcon />
        <Typography variant="textBody" sx={{ color: "#FFF !important" }}>
            Nenhum dado encontrado.
        </Typography>
    </Box>
)

const DataGridScrollTable = ({
    columns,
    rows,
    onRowClick,
    loading,
    pointerCursor,
    sx,
    ...props
}) => {
    const apiRef = useGridApiRef()

    const [filterModel, setFilterModel] = useState({ items: [] })
    const [pageSelected, setPageSelected] = useState(1)
    const [pageSize, setPageSize] = useState(25)

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            return filterModel.items.every((f) => {
                if (!f.value) return true
                const cellValue = String(row[f.field] ?? "").toLowerCase()
                return cellValue.includes(String(f.value).toLowerCase())
            })
        })
    }, [rows, filterModel])

    const totalPages = Math.ceil(filteredRows.length / pageSize)
    const startIndex = (pageSelected - 1) * pageSize
    const endIndex = startIndex + pageSize
    const visibleRows = filteredRows.slice(startIndex, endIndex)

    useEffect(() => {
        setPageSelected(1)
    }, [filterModel])

    useEffect(() => {
        if (pageSelected > totalPages) {
            setPageSelected(totalPages || 1)
        }
    }, [filteredRows, totalPages, pageSelected])

    const baseSx = {
        width: "100%",
        "&.MuiDataGrid-root": { border: "none !important" },
        "& .MuiDataGrid-withBorderColor": {
            borderColor: "transparent !important",
        },

        "& .MuiDataGrid-columnHeaders": {
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#2E3448",
            borderBottom: "none !important",
        },

        "& .MuiDataGrid-columnSeparator": {
            display: "none !important",
        },

        "& .MuiDataGrid-cell": {
            fontFamily: "'Roboto', sans-serif",
            fontSize: "14px",
            color: "#FFFFFF !important",
            borderBottom: "none !important",
        },

        "& .MuiDataGrid-columnHeaderTitle": {
            fontFamily: "'Titillium Web'",
            fontWeight: 700,
            fontSize: "11px",
            textTransform: "uppercase",
            color: "#FFFFFF",
        },

        "& .even": { backgroundColor: "rgba(255,255,255,0.04)" },
        "& .odd": { backgroundColor: "rgba(255,255,255,0.09)" },

        "& .MuiDataGrid-cell:focus": { outline: "none" },

        ...(pointerCursor && {
            "& .pointerCursor .MuiDataGrid-cell": {
                cursor: "pointer",
            },
        }),
    }

    const mergedSx = deepmerge(baseSx, sx || {})

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <DataGrid
                apiRef={apiRef}
                rows={loading ? [] : visibleRows}
                columns={columns}
                loading={loading}
                autoHeight={false}
                hideFooter
                disableVirtualization={false}
                localeText={localeBRDataGridText}
                onRowClick={onRowClick}
                filterModel={filterModel}
                onFilterModelChange={setFilterModel}
                getRowClassName={(params) => {
                    const base =
                        params.indexRelativeToCurrentPage % 2 === 0
                            ? "even"
                            : "odd"
                    return pointerCursor
                        ? base + " pointerCursor"
                        : base
                }}
                slots={{
                    columnMenu: CustomColumnMenu,
                    noRowsOverlay: NoRowOverlay,
                    loadingOverlay: (props) => (
                        <CustomLoadingOverlay
                            columns={columns}
                            {...props}
                        />
                    ),
                }}
                sx={{
                    ...mergedSx,
                    flex: 1,
                }}
                {...props}
            />

            {visibleRows.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <FooterPagination
                        totalPages={totalPages}
                        page={pageSelected}
                        handlePageChange={(value) => {
                            if (value === pageSelected) return
                            setPageSelected(value)
                        }}
                        handlePageSizeChange={(value) => {
                            setPageSize(value)
                            setPageSelected(1)
                        }}
                        valueSelectPaginationValue={pageSize}
                    />
                </Box>
            )}
        </Box>
    )
}

DataGridScrollTable.propTypes = {
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    onRowClick: PropTypes.func,
    pointerCursor: PropTypes.bool,
    height: PropTypes.number,
    sx: PropTypes.object,
}

export default DataGridScrollTable